import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useSEO } from '../hooks/useSEO';
import api from '../services/api';

const PageContainer = styled.div`
  max-width: 520px;
  margin: 2rem auto;
  background: var(--white);
  border-radius: var(--radius-xl);
  box-shadow: var(--shadow-xl);
  overflow: hidden;
`;

const Header = styled.div`
  background: linear-gradient(135deg, var(--primary-blue) 0%, var(--primary-blue-dark) 50%, var(--brand-red) 100%);
  color: var(--white);
  padding: 2rem;
  text-align: center;
`;

const Title = styled.h1`
  font-size: 1.75rem;
  font-weight: 700;
  margin: 0 0 0.5rem 0;
`;

const Subtitle = styled.p`
  margin: 0;
  font-size: 0.95rem;
  opacity: 0.95;
  line-height: 1.5;
`;

const Body = styled.div`
  padding: 2rem;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
`;

const Label = styled.label`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  font-weight: 600;
  color: var(--text-primary);
`;

const Input = styled.input`
  padding: 0.75rem;
  border: 2px solid var(--border-color);
  border-radius: var(--radius-md);
  font-size: 1rem;
  font-weight: 400;

  &:focus {
    outline: none;
    border-color: var(--primary-blue);
    box-shadow: 0 0 0 3px rgb(0 78 158 / 0.1);
  }
`;

const HelpText = styled.p`
  margin: 0;
  font-size: 0.875rem;
  color: var(--text-secondary);
  line-height: 1.5;
`;

const SubmitButton = styled.button`
  width: 100%;
  background: linear-gradient(135deg, #043663 0%, #0a4d7a 100%);
  color: #ffffff;
  border: none;
  padding: 0.9rem 1rem;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: 0 8px 16px -8px rgba(4, 54, 99, 0.45);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const SecondaryButton = styled.button`
  width: 100%;
  background: #f3f4f6;
  color: #374151;
  border: none;
  padding: 0.85rem 1rem;
  border-radius: 8px;
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;

  &:hover {
    background: #e5e7eb;
  }
`;

const MessageBox = styled.div`
  padding: 1rem 1.1rem;
  border-radius: 10px;
  margin-bottom: 1.25rem;
  font-size: 0.95rem;
  line-height: 1.55;

  ${(props) =>
    props.$variant === 'success'
      ? `
    background: #d1fae5;
    color: #065f46;
    border: 1px solid #a7f3d0;
  `
      : props.$variant === 'error'
        ? `
    background: #fee2e2;
    color: #991b1b;
    border: 1px solid #fecaca;
  `
        : `
    background: #eff6ff;
    color: #1e3a5f;
    border: 1px solid #bfdbfe;
  `}
`;

const MessageTitle = styled.h2`
  margin: 0 0 0.5rem 0;
  font-size: 1.1rem;
`;

const MessageBody = styled.p`
  margin: 0 0 0.75rem 0;
`;

const ActionRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  margin-top: 1rem;
`;

const TextLink = styled(Link)`
  display: block;
  text-align: center;
  color: var(--primary-blue);
  font-weight: 600;
  text-decoration: none;
  margin-top: 1rem;

  &:hover {
    text-decoration: underline;
  }
`;

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const PlayerLoginHelpPage = () => {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [view, setView] = useState('form');
  const [errorMessage, setErrorMessage] = useState('');

  useSEO({
    title: t('playerLoginHelp.pageTitle'),
    description: t('playerLoginHelp.metaDescription'),
    canonical: 'https://helveticpadelseries.ch/player-login-help',
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    const trimmed = email.trim();

    if (!trimmed) {
      setErrorMessage(t('playerLoginHelp.emailRequired'));
      return;
    }
    if (!EMAIL_PATTERN.test(trimmed)) {
      setErrorMessage(t('playerLoginHelp.emailInvalid'));
      return;
    }

    setLoading(true);
    setErrorMessage('');

    try {
      const response = await api.post('/auth/player-resend-credentials', { email: trimmed });
      if (response?.success) {
        setView('success');
      } else {
        setErrorMessage(t('playerLoginHelp.genericError'));
      }
    } catch (error) {
      const code = error.response?.data?.code;
      if (error.response?.status === 404 || code === 'PLAYER_NOT_FOUND') {
        setView('notFound');
        return;
      }
      if (error.response?.status === 429 || code === 'RATE_LIMITED') {
        setErrorMessage(t('playerLoginHelp.rateLimited'));
        return;
      }
      if (code === 'EMAIL_SEND_FAILED') {
        setErrorMessage(t('playerLoginHelp.sendFailed'));
        return;
      }
      if (code === 'INVALID_EMAIL') {
        setErrorMessage(t('playerLoginHelp.emailInvalid'));
        return;
      }
      setErrorMessage(t('playerLoginHelp.genericError'));
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setView('form');
    setErrorMessage('');
  };

  return (
    <PageContainer>
      <Header>
        <Title>{t('playerLoginHelp.title')}</Title>
        <Subtitle>{t('playerLoginHelp.subtitle')}</Subtitle>
      </Header>

      <Body>
        {view === 'success' && (
          <>
            <MessageBox $variant="success">
              <MessageTitle>{t('playerLoginHelp.successTitle')}</MessageTitle>
              <MessageBody>{t('playerLoginHelp.successBody')}</MessageBody>
            </MessageBox>
            <ActionRow>
              <SecondaryButton type="button" onClick={resetForm}>
                {t('playerLoginHelp.tryAnotherEmail')}
              </SecondaryButton>
            </ActionRow>
            <TextLink to="/login">{t('playerLoginHelp.backToLogin')}</TextLink>
          </>
        )}

        {view === 'notFound' && (
          <>
            <MessageBox $variant="error">
              <MessageTitle>{t('playerLoginHelp.notFoundTitle')}</MessageTitle>
              <MessageBody>{t('playerLoginHelp.notFoundBody')}</MessageBody>
              <MessageBody>{t('playerLoginHelp.notFoundSupport')}</MessageBody>
            </MessageBox>
            <ActionRow>
              <SecondaryButton type="button" onClick={resetForm}>
                {t('playerLoginHelp.tryAnotherEmail')}
              </SecondaryButton>
              <SubmitButton as={Link} to="/#contact">
                {t('playerLoginHelp.contactSupport')}
              </SubmitButton>
            </ActionRow>
            <TextLink to="/login">{t('playerLoginHelp.backToLogin')}</TextLink>
          </>
        )}

        {view === 'form' && (
          <>
            {errorMessage && (
              <MessageBox $variant="error">
                <MessageBody>{errorMessage}</MessageBody>
              </MessageBox>
            )}

            <Form onSubmit={handleSubmit}>
              <Label htmlFor="player-login-help-email">
                {t('playerLoginHelp.emailLabel')}
                <Input
                  id="player-login-help-email"
                  type="email"
                  autoComplete="email"
                  value={email}
                  onChange={(ev) => setEmail(ev.target.value)}
                  placeholder={t('playerLoginHelp.emailPlaceholder')}
                  disabled={loading}
                  required
                />
              </Label>

              <HelpText>{t('playerLoginHelp.instructions')}</HelpText>

              <SubmitButton type="submit" disabled={loading}>
                {loading ? t('playerLoginHelp.submitting') : t('playerLoginHelp.submit')}
              </SubmitButton>
            </Form>

            <TextLink to="/login">{t('playerLoginHelp.backToLogin')}</TextLink>
          </>
        )}
      </Body>
    </PageContainer>
  );
};

export default PlayerLoginHelpPage;
